import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE, DASHBOARD_URL } from '../constants/api';
import { clearPreferredPortal } from '../utils/portalRouting';

export interface User {
  id: number;
  name: string;
  email: string;
  /** From API serialization; omit or false for older cached localStorage users. */
  is_admin?: boolean;
  /** Portal role from login/register (`customer` | `installer` | `admin`). */
  role?: 'customer' | 'installer' | 'admin';
  /** True when this user owns at least one submitted customer quote. */
  hasActiveQuote?: boolean;
  email_verified_at?: string;
  created_at: string;
  updated_at: string;
  /** Present when the API loads the `contact` relation (e.g. login/register). */
  contact?: {
    id?: number;
    user_id?: number;
    phone?: string | null;
    phone_verified_at?: string | null;
  } | null;
}

export type AuthResult = { success: boolean; message?: string; user?: User };

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<AuthResult>;
  register: (name: string, email: string, password: string, password_confirmation: string) => Promise<AuthResult>;
  /** Persists phone on `contacts` via `PUT /api/auth/profile`. Updates context and localStorage on success. */
  syncContactPhone: (phone: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function fetchSanctumCookie(): Promise<void> {
  await fetch(`${API_BASE}/sanctum/csrf-cookie`, {
    method: 'GET',
    credentials: 'include',
  });
}

/**
 * Create a Laravel web session from the stored Sanctum token so a full-page
 * visit to the backend (e.g. Inertia dashboard) is authenticated. Needed when
 * the SPA origin is not in Sanctum's stateful list (common with Herd *.test).
 */
// eslint-disable-next-line react-refresh/only-export-components
export async function establishBackendWebSession(): Promise<{ success: boolean; message?: string }> {
  const authToken = localStorage.getItem('auth_token');
  if (!authToken) {
    return { success: false, message: 'No auth token' };
  }
  try {
    const response = await fetch(`${API_BASE}/auth/establish-session`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        Accept: 'application/json',
      },
      credentials: 'include',
    });
    const data = (await response.json().catch(() => ({}))) as { message?: string };
    if (!response.ok) {
      return {
        success: false,
        message: data.message || 'Could not start dashboard session',
      };
    }
    return { success: true };
  } catch {
    return { success: false, message: 'Network error' };
  }
}

/**
 * Bridge token → Laravel web session, then navigate to a path on the dashboard host.
 */
// eslint-disable-next-line react-refresh/only-export-components
export async function openDashboardPath(path: string): Promise<{ success: boolean; message?: string }> {
  const session = await establishBackendWebSession();
  if (!session.success) {
    window.alert(session.message || 'Could not open the dashboard. Try signing in again.');
    return session;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  window.location.assign(`${DASHBOARD_URL}${normalized}`);
  return { success: true };
}

// Auth provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is authenticated
  const isAuthenticated = !!user && !!token;

  // Initialize auth state from localStorage, then refresh role fields from the API.
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    const storedUserRaw = localStorage.getItem('auth_user');
    let parsed: User | null = null;

    if (storedToken && storedUserRaw) {
      try {
        parsed = JSON.parse(storedUserRaw) as User;
        setToken(storedToken);
        setUser(parsed);
      } catch {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
      }
    }

    setIsLoading(false);

    if (!storedToken || !parsed) return;

    let cancelled = false;
    const cached = parsed;
    void (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/auth/user`, {
          headers: {
            Authorization: `Bearer ${storedToken}`,
            Accept: 'application/json',
          },
          credentials: 'include',
        });
        if (cancelled) return;
        if (response.status === 401) {
          setUser(null);
          setToken(null);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          return;
        }
        const data = await response.json();
        if (data.success && data.data) {
          const merged: User = {
            ...cached,
            name: data.data.name ?? cached.name,
            email: data.data.email ?? cached.email,
            role: data.data.role,
            hasActiveQuote: Boolean(data.data.hasActiveQuote),
          };
          setUser(merged);
          localStorage.setItem('auth_user', JSON.stringify(merged));
        }
      } catch {
        // Keep the cached session if the refresh fails.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Login function
  const login = async (email: string, password: string): Promise<AuthResult> => {
    try {
      await fetchSanctumCookie();
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success) {
        const { user: userData, token: authToken } = data.data as { user: User; token: string };
        setUser(userData);
        setToken(authToken);
        localStorage.setItem('auth_token', authToken);
        localStorage.setItem('auth_user', JSON.stringify(userData));
        return { success: true, user: userData };
      } else {
        let message = data.message || 'Login failed';
        if (data.errors) {
          const firstKey = Object.keys(data.errors)[0];
          if (firstKey) {
            const firstMsg = Array.isArray(data.errors[firstKey]) ? data.errors[firstKey][0] : String(data.errors[firstKey]);
            message = firstMsg || message;
          }
        }
        return { success: false, message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  };

  // Register function
  const register = async (name: string, email: string, password: string, password_confirmation: string): Promise<AuthResult> => {
    try {
      await fetchSanctumCookie();
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ name, email, password, password_confirmation }),
      });

      const data = await response.json();

      if (data.success) {
        const { user: userData, token: authToken } = data.data as { user: User; token: string };
        setUser(userData);
        setToken(authToken);
        localStorage.setItem('auth_token', authToken);
        localStorage.setItem('auth_user', JSON.stringify(userData));
        return { success: true, user: userData };
      } else {
        let message = data.message || 'Registration failed';
        if (data.errors) {
          // Surface specific validation like duplicate email
          if (data.errors.email) {
            const firstEmailErr = Array.isArray(data.errors.email) ? data.errors.email[0] : String(data.errors.email);
            message = firstEmailErr || message;
          } else {
            const firstKey = Object.keys(data.errors)[0];
            if (firstKey) {
              const firstMsg = Array.isArray(data.errors[firstKey]) ? data.errors[firstKey][0] : String(data.errors[firstKey]);
              message = firstMsg || message;
            }
          }
        }
        return { success: false, message };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  };

  const syncContactPhone = async (phone: string): Promise<{ success: boolean; message?: string }> => {
    const authToken = token ?? localStorage.getItem('auth_token');
    if (!authToken) {
      return { success: false, message: 'Not authenticated' };
    }
    try {
      await fetchSanctumCookie();
      const response = await fetch(`${API_BASE}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(authToken),
        },
        credentials: 'include',
        body: JSON.stringify({ phone: phone.trim() }),
      });

      const data = await response.json();

      if (data.success && data.data?.user) {
        const userData = data.data.user as User;
        const merged: User = {
          ...user,
          ...userData,
          role: user?.role ?? userData.role,
          hasActiveQuote: user?.hasActiveQuote ?? userData.hasActiveQuote,
        };
        setUser(merged);
        localStorage.setItem('auth_user', JSON.stringify(merged));
        return { success: true };
      }

      let message = data.message || 'Could not save phone number';
      if (data.errors) {
        const firstKey = Object.keys(data.errors)[0];
        if (firstKey) {
          const firstMsg = Array.isArray(data.errors[firstKey])
            ? data.errors[firstKey][0]
            : String(data.errors[firstKey]);
          message = firstMsg || message;
        }
      }
      return { success: false, message };
    } catch (error) {
      console.error('syncContactPhone error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      clearPreferredPortal();
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    syncContactPhone,
    logout,
    isLoading,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to get auth headers
// eslint-disable-next-line react-refresh/only-export-components
export const getAuthHeaders = (token: string | null) => {
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
};
